/**
 * @fileoverview Banner displayed on all pages when viewing a ceased (archived) budget.
 * Shows cease date and explanation that the budget is read-only.
 * For owners, shows a "Restore Budget" button.
 */

import { budgetAPI } from '../../services/api';
import styles from './CeasedBudgetBanner.module.css';

/**
 * CeasedBudgetBanner Component
 *
 * Warning banner for ceased budgets with restore functionality.
 *
 * Props:
 * - ceaseDate (string): ISO date string when budget was ceased
 * - isOwner (boolean): Whether current user is the budget owner
 * - onRestore (function): Optional callback when budget is restored
 */
function CeasedBudgetBanner({ ceaseDate, isOwner, onRestore }) {
    const handleRestore = async () => {
        if (!onRestore) return;
        await onRestore();
    };

    // Format cease date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className={styles.banner}>
            <div className={styles.content}>
                <div className={styles.icon}>⚠️</div>
                <div className={styles.message}>
                    <div className={styles.title}>
                        This budget was ceased on {formatDate(ceaseDate)}
                    </div>
                    <div className={styles.description}>
                        This budget is archived and in read-only mode. You can view all data and run reports, but cannot add, edit, or delete anything.
                    </div>
                </div>
                {isOwner && onRestore && (
                    <button
                        onClick={handleRestore}
                        className={styles.restoreButton}
                    >
                        Restore Budget
                    </button>
                )}
            </div>
        </div>
    );
}

export default CeasedBudgetBanner;
